import { ChangeEvent, useState } from 'react';
import React from 'react';

import { HelpDrawerLink } from '../../../../components/HelpDrawerLink';
import { Drawer } from '../../../../components/Drawer';
import TextField from '../../../../components/TextField';
import { useBlurValidation } from '../../../../hooks/useBlurValidation';
import { WorkflowSubStepProps } from '../../config';
import { USER_CATEGORY } from '../../constants';
import { email, phoneNumber, required } from '../../utils/fieldValidation';
import './ContactInformationPage.css';

import { formatUsPhoneNumber, normalizeUsPhoneNumber } from '@/utils';

export interface ContactInformationFormData {
  registrationEmail: string;
  phoneNumber: string;
  userCategory: string;
}

function ContactInformationPage({ data, updateData, fieldErrors }: WorkflowSubStepProps<ContactInformationFormData>) {
  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false);
  const baseTestId = 'contact-information-page';

  const {
    handleBlur,
    handleChange: handleValidationChange,
    getFieldError,
  } = useBlurValidation({
    externalErrors: fieldErrors,
    formData: data,
    rules: [
      { field: 'registrationEmail', label: 'Registration Email', validationRules: [required, email] },
      { field: 'phoneNumber', label: 'Phone Number', validationRules: [required, phoneNumber] },
      { field: 'userCategory', label: 'User Category', validationRules: [required] },
    ],
  });

  const handleChange = (field: keyof ContactInformationFormData, value: string) => {
    updateData({
      ...data,
      [field]: value,
    });
    handleValidationChange(field, value);
  };

  const handlePhoneKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const cursorPos = input.selectionStart || 0;
    const value = input.value;

    if (e.key === 'Backspace' && cursorPos > 0) {
      const charBeforeCursor = value[cursorPos - 1];
      if (
        charBeforeCursor === '(' ||
        charBeforeCursor === ')' ||
        charBeforeCursor === ' ' ||
        charBeforeCursor === '-'
      ) {
        e.preventDefault();
        const digitsOnly = data.phoneNumber || '';
        const newDigits = digitsOnly.slice(0, -1);
        updateData({
          ...data,
          phoneNumber: newDigits,
        });
      }
    }
  };

  const handlePhoneChange = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    const limitedDigits = normalizeUsPhoneNumber(input).slice(0, 10);

    updateData({
      ...data,
      phoneNumber: limitedDigits,
    });
    handleValidationChange('phoneNumber', limitedDigits);
  };

  return (
    <div data-testid={baseTestId}>
      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-registration-email`}>
        <TextField
          required
          data-testid={`${baseTestId}-registration-email-field`}
          errorMessage={getFieldError('registrationEmail')}
          hint='Enter the email address where your registration invitation was sent'
          label='Registration Email'
          name='registrationEmail'
          tooltipText='Enter the email address where your registration invitation was sent'
          type='email'
          value={data.registrationEmail || ''}
          onBlur={() => handleBlur('registrationEmail')}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('registrationEmail', e.target.value)}
        />
      </div>

      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-phone`}>
        <TextField
          required
          data-testid={`${baseTestId}-phone-field`}
          errorMessage={getFieldError('phoneNumber')}
          hint='(XXX) XXX-XXXX'
          label='Personal Phone Number'
          name='phoneNumber'
          type='tel'
          value={formatUsPhoneNumber(data.phoneNumber || '')}
          onBlur={() => handleBlur('phoneNumber')}
          onChange={handlePhoneChange}
          onKeyDown={handlePhoneKeyDown}
        />
      </div>

      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-user-category`}>
        <TextField
          readOnly
          className='contact-information-page__user-category-field'
          data-testid={`${baseTestId}-user-category-field`}
          label='User Category'
          name='userCategory'
          tooltipText='	The user category defines the type of employee you are, such as CMS Employee, Contractor, Federal Agency, or Business Partner.'
          value={USER_CATEGORY.DISPLAY}
        />
      </div>

      <div
        className='ds-u-border-top--2 ds-u-padding-top--3 ds-u-margin-top--4 field-max-width'
        data-testid={`${baseTestId}-help`}>
        <HelpDrawerLink
          data-testid={`${baseTestId}-help-link`}
          onClick={() => setIsHelpDrawerOpen(true)}
        />
      </div>

      <Drawer
        data-testid={`${baseTestId}-drawer`}
        heading='Help: Contact Information'
        isOpen={isHelpDrawerOpen}
        onClose={() => setIsHelpDrawerOpen(false)}>
        <div className='ds-u-padding--3'>
          <h3 className='ds-u-margin-top--0'>Get Help for Contact Information</h3>
          <p>
            Please provide your contact details so we can get in touch with you about your application and set up your
            account.
          </p>

          <h4 className='ds-u-margin-top--3'>Registration Email</h4>
          <p>
            Enter the email address that your invitation was sent to. This is the primary email address that we will use
            to communicate with you about your CMS user ID application. Make sure this email address is active, and you
            check it regularly.
          </p>
          <p className='ds-u-margin-top--2'>
            <strong>Important:</strong> This should be the same email address you used to receive your invitation to
            apply for a CMS user ID.
          </p>

          <h4 className='ds-u-margin-top--3'>Personal Phone Number</h4>
          <p>
            Enter your personal phone number in the format (XXX) XXX-XXXX. This number will be used to contact you if we
            need to verify information or if there are any issues with your application.
          </p>
          <p className='ds-u-margin-top--2'>
            Please provide a number where you can be reliably reached during business hours (9 AM - 5 PM ET).
          </p>

          <h4 className='ds-u-margin-top--3'>User Category</h4>
          <p>
            The user category defines the type of employee you are. This determines the type of access you will be
            granted. These may include:
          </p>
          <ul className='ds-u-margin-top--2'>
            <li>
              <strong>Developer:</strong> Software developers working on CMS systems
            </li>
            <li>
              <strong>Federal Employee:</strong> Direct employees of the federal government
            </li>
            <li>
              <strong>Contractor:</strong> Contract workers providing services to CMS
            </li>
            <li>
              <strong>State Employee:</strong> State government employees working with CMS programs
            </li>
            <li>
              <strong>Other:</strong> Other categories not listed above
            </li>
          </ul>
        </div>
      </Drawer>
    </div>
  );
}

export default ContactInformationPage;
